import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';

import { providerConfig } from '@team/antd-config-provider';
import { App as AntdApp, ConfigProvider, ThemeConfig } from 'antd';

import { useTheme } from '@/hooks/useTheme';
import { router } from '@/routes';

import { UserProvider } from './UserProvider';

const antdTheme: ThemeConfig = {
  ...providerConfig.theme,
  token: {
    ...providerConfig.theme?.token,
    colorLink: 'rgba(17, 19, 20, 1)',
    colorLinkHover: 'rgba(17, 19, 20, .6)',
  },
};

const ErrorFallback = () => {
  return <div>error</div>;
};

export const App = () => {
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState<string>('');

  // 初始化主题，确保从 localStorage 读取并应用
  useTheme();

  useEffect(() => {
    // 监听语言变化
    const handleLanguageChange = (lng: string) => {
      console.log('语言检测结果:', lng);
      setCurrentLanguage(lng);
      setIsLoading(false);
    };

    // 如果 i18n 已经初始化完成
    if (i18n.isInitialized) {
      handleLanguageChange(i18n.language);
    } else {
      // 等待 i18n 初始化完成
      i18n.on('initialized', handleLanguageChange);
    }

    // 监听语言切换事件
    i18n.on('languageChanged', handleLanguageChange);

    // 清理事件监听器
    return () => {
      i18n.off('initialized', handleLanguageChange);
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  // 判断是否为中文
  const isChinese = (language: string): boolean => {
    const chineseLanguages = ['zh', 'zh-CN', 'zh-Hans', 'zh-Hant', 'zh-TW', 'zh-HK'];
    return chineseLanguages.some((lang) => language.toLowerCase().startsWith(lang.toLowerCase()));
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">正在检测语言...</p>
        </div>
      </div>
    );
  }

  // 根据语言渲染对应组件
  const shouldRenderChinese = isChinese(currentLanguage);

  // 开发环境下显示调试信息
  if (import.meta.env.DEV) {
    console.log('SmartHome 渲染信息:', {
      currentLanguage,
      shouldRenderChinese,
      component: shouldRenderChinese ? 'HomeCN' : 'Home',
    });
  }

  return (
    <UserProvider>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, info) => console.error(error, info)}
      >
        <ConfigProvider {...providerConfig} theme={antdTheme}>
          <Toaster
            position="top-center"
            toastOptions={{
              style: {
                whiteSpace: 'nowrap',
                maxWidth: 'none',
                fontSize: '14px',
                padding: '4px 8px',
              },
            }}
            containerStyle={{
              top: '50px',
            }}
          />
          <AntdApp
            className={`bg-background-primary ${i18n.resolvedLanguage === 'zh' ? 'font-pingfang' : 'font-manrope'}`}
          >
            <RouterProvider router={router} />
          </AntdApp>
        </ConfigProvider>
      </ErrorBoundary>
    </UserProvider>
  );
};
